<template>
  <div class="p-6 bg-gray-100 min-h-screen">
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-800">角色管理</h1>
        <button 
          @click="createRole"
          class="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors duration-300 flex items-center"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          新增角色
        </button>
      </div>

      <!-- 角色列表 -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div v-for="(role, index) in roles" :key="index" class="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <div class="flex items-center">
              <div 
                :class="[
                  'w-10 h-10 rounded-full flex items-center justify-center mr-3',
                  role.code === 'admin' ? 'bg-red-100 text-red-600' : 
                  role.code === 'manager' ? 'bg-blue-100 text-blue-600' : 
                  'bg-green-100 text-green-600'
                ]"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-800">{{ role.name }}</h3>
            </div>
            <div class="flex space-x-2">
              <button 
                @click="editRole(role)" 
                class="p-1 text-blue-600 hover:text-blue-800"
                :disabled="role.code === 'admin'"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
              <button 
                @click="deleteRole(role)" 
                class="p-1 text-red-600 hover:text-red-800"
                :disabled="role.code === 'admin' || role.code === 'manager' || role.code === 'employee'"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-600 mb-4">{{ role.description }}</p>
            <div class="mb-4">
              <p class="text-sm font-medium text-gray-700 mb-2">权限列表</p>
              <div class="grid grid-cols-2 gap-2">
                <div v-for="(perm, permIndex) in role.permissions" :key="permIndex" class="text-xs text-gray-800 bg-gray-100 rounded px-2 py-1">
                  {{ perm.name }}
                </div>
              </div>
            </div>
            <div class="flex justify-between items-center text-sm text-gray-500">
              <span>角色代码: {{ role.code }}</span>
              <span>用户数: {{ role.userCount }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 角色表单对话框 -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-2xl w-full">
        <!-- 对话框头部 -->
        <div class="flex justify-between items-center border-b px-6 py-4">
          <h3 class="text-lg font-semibold text-gray-800">{{ isEdit ? '编辑角色' : '新增角色' }}</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-500">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <!-- 对话框内容 -->
        <div class="px-6 py-4">
          <form @submit.prevent="submitForm">
            <div class="mb-4">
              <label for="name" class="block text-sm font-medium text-gray-700 mb-1">角色名称</label>
              <input 
                type="text" 
                id="name" 
                v-model="form.name" 
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>
            
            <div class="mb-4">
              <label for="code" class="block text-sm font-medium text-gray-700 mb-1">角色代码</label>
              <input 
                type="text" 
                id="code" 
                v-model="form.code" 
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                required
                :disabled="isEdit"
              />
            </div>

            <div class="mb-4">
              <label for="description" class="block text-sm font-medium text-gray-700 mb-1">角色描述</label>
              <textarea 
                id="description" 
                v-model="form.description" 
                rows="2"
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                required
              ></textarea>
            </div>

            <div class="mb-4">
              <p class="block text-sm font-medium text-gray-700 mb-2">角色权限</p>
              <div class="border border-gray-300 rounded-md p-4 max-h-60 overflow-y-auto">
                <div v-for="(module, moduleIndex) in permissionModules" :key="moduleIndex" class="mb-4">
                  <div class="flex items-center mb-2">
                    <input 
                      :id="'module-' + moduleIndex"
                      type="checkbox"
                      v-model="moduleSelections[moduleIndex]"
                      @change="toggleModuleSelection(moduleIndex)"
                      class="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label :for="'module-' + moduleIndex" class="ml-2 block text-sm font-medium text-gray-700">
                      {{ module.name }}
                    </label>
                  </div>
                  <div class="pl-6 grid grid-cols-2 gap-2">
                    <div v-for="(perm, permIndex) in module.permissions" :key="permIndex" class="flex items-center">
                      <input 
                        :id="'perm-' + moduleIndex + '-' + permIndex"
                        type="checkbox"
                        v-model="form.permissions"
                        :value="perm"
                        class="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <label :for="'perm-' + moduleIndex + '-' + permIndex" class="ml-2 block text-sm text-gray-600">
                        {{ perm.name }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button 
                type="button" 
                @click="closeModal" 
                class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                取消
              </button>
              <button 
                type="submit" 
                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {{ isEdit ? '保存' : '创建' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RoleManagement',
  data() {
    return {
      showModal: false,
      isEdit: false,
      currentRoleCode: null,
      form: {
        name: '',
        code: '',
        description: '',
        permissions: []
      },
      moduleSelections: {},
      roles: [
        {
          name: '系统管理员',
          code: 'admin',
          description: '系统最高权限角色，可以管理所有功能和数据',
          permissions: [
            { code: 'user_view', name: '查看用户' },
            { code: 'user_create', name: '创建用户' },
            { code: 'user_edit', name: '编辑用户' },
            { code: 'user_delete', name: '删除用户' },
            { code: 'role_view', name: '查看角色' },
            { code: 'role_create', name: '创建角色' },
            { code: 'role_edit', name: '编辑角色' },
            { code: 'role_delete', name: '删除角色' },
            { code: 'dept_view', name: '查看部门' },
            { code: 'dept_create', name: '创建部门' },
            { code: 'dept_edit', name: '编辑部门' },
            { code: 'dept_delete', name: '删除部门' },
            { code: 'log_view', name: '查看日志' },
            { code: 'score_view', name: '查看积分' },
            { code: 'score_edit', name: '编辑积分' },
            { code: 'score_review', name: '审核积分' },
            { code: 'formula_view', name: '查看公式' },
            { code: 'formula_edit', name: '编辑公式' },
          ],
          userCount: 1
        },
        {
          name: '部门管理员',
          code: 'manager',
          description: '部门管理员角色，可以管理本部门的积分和员工数据',
          permissions: [
            { code: 'user_view', name: '查看用户' },
            { code: 'dept_view', name: '查看部门' },
            { code: 'score_view', name: '查看积分' },
            { code: 'score_review', name: '审核积分' },
            { code: 'formula_view', name: '查看公式' },
          ],
          userCount: 3
        },
        {
          name: '普通员工',
          code: 'employee',
          description: '普通员工角色，可以上报积分和查看自己的积分数据',
          permissions: [
            { code: 'score_view', name: '查看积分' },
            { code: 'score_submit', name: '上报积分' },
          ],
          userCount: 15
        },
        {
          name: '积分审核员',
          code: 'reviewer',
          description: '积分审核员角色，负责审核员工上报的积分',
          permissions: [
            { code: 'score_view', name: '查看积分' },
            { code: 'score_review', name: '审核积分' },
          ],
          userCount: 5
        }
      ],
      permissionModules: [
        {
          name: '用户管理',
          permissions: [
            { code: 'user_view', name: '查看用户' },
            { code: 'user_create', name: '创建用户' },
            { code: 'user_edit', name: '编辑用户' },
            { code: 'user_delete', name: '删除用户' }
          ]
        },
        {
          name: '角色管理',
          permissions: [
            { code: 'role_view', name: '查看角色' },
            { code: 'role_create', name: '创建角色' },
            { code: 'role_edit', name: '编辑角色' },
            { code: 'role_delete', name: '删除角色' }
          ]
        },
        {
          name: '部门管理',
          permissions: [
            { code: 'dept_view', name: '查看部门' },
            { code: 'dept_create', name: '创建部门' },
            { code: 'dept_edit', name: '编辑部门' },
            { code: 'dept_delete', name: '删除部门' }
          ]
        },
        {
          name: '日志管理',
          permissions: [
            { code: 'log_view', name: '查看日志' }
          ]
        },
        {
          name: '积分管理',
          permissions: [
            { code: 'score_view', name: '查看积分' },
            { code: 'score_submit', name: '上报积分' },
            { code: 'score_edit', name: '编辑积分' },
            { code: 'score_review', name: '审核积分' }
          ]
        },
        {
          name: '公式管理',
          permissions: [
            { code: 'formula_view', name: '查看公式' },
            { code: 'formula_edit', name: '编辑公式' }
          ]
        }
      ]
    }
  },
  created() {
    // 初始化模块选择状态
    this.initModuleSelections();
  },
  methods: {
    initModuleSelections() {
      this.permissionModules.forEach((module, index) => {
        this.$set(this.moduleSelections, index, false);
      });
    },
    toggleModuleSelection(moduleIndex) {
      const module = this.permissionModules[moduleIndex];
      const isSelected = this.moduleSelections[moduleIndex];
      
      // 如果选中模块，添加该模块下所有权限
      if (isSelected) {
        module.permissions.forEach(perm => {
          if (!this.form.permissions.some(p => p.code === perm.code)) {
            this.form.permissions.push(perm);
          }
        });
      } else {
        // 如果取消选中模块，移除该模块下所有权限
        this.form.permissions = this.form.permissions.filter(perm => 
          !module.permissions.some(p => p.code === perm.code)
        );
      }
    },
    createRole() {
      this.isEdit = false;
      this.form = {
        name: '',
        code: '',
        description: '',
        permissions: []
      };
      // 重置模块选择状态
      this.initModuleSelections();
      this.showModal = true;
    },
    editRole(role) {
      if (role.code === 'admin') {
        alert('系统管理员角色不可编辑');
        return;
      }
      
      this.isEdit = true;
      this.currentRoleCode = role.code;
      
      // 复制角色数据到表单
      this.form = {
        name: role.name,
        code: role.code,
        description: role.description,
        permissions: [...role.permissions]
      };
      
      // 更新模块选择状态
      this.initModuleSelections();
      this.updateModuleSelections();
      
      this.showModal = true;
    },
    updateModuleSelections() {
      this.permissionModules.forEach((module, index) => {
        // 检查该模块下的所有权限是否都被选中
        const allSelected = module.permissions.every(perm => 
          this.form.permissions.some(p => p.code === perm.code)
        );
        this.$set(this.moduleSelections, index, allSelected);
      });
    },
    deleteRole(role) {
      if (['admin', 'manager', 'employee'].includes(role.code)) {
        alert('系统内置角色不可删除');
        return;
      }
      
      if (confirm(`确定要删除角色 "${role.name}" 吗？`)) {
        // 从列表中移除角色
        const index = this.roles.findIndex(r => r.code === role.code);
        if (index !== -1) {
          this.roles.splice(index, 1);
          alert('角色删除成功');
        }
      }
    },
    closeModal() {
      this.showModal = false;
    },
    submitForm() {
      if (this.form.permissions.length === 0) {
        alert('请至少选择一个权限');
        return;
      }
      
      if (this.isEdit) {
        // 更新现有角色
        const index = this.roles.findIndex(role => role.code === this.currentRoleCode);
        if (index !== -1) {
          this.roles[index] = {
            ...this.roles[index],
            name: this.form.name,
            description: this.form.description,
            permissions: [...this.form.permissions]
          };
        }
        alert('角色更新成功');
      } else {
        // 添加新角色
        // 检查角色代码是否已存在
        if (this.roles.some(role => role.code === this.form.code)) {
          alert('角色代码已存在，请使用其他代码');
          return;
        }
        
        const newRole = {
          name: this.form.name,
          code: this.form.code,
          description: this.form.description,
          permissions: [...this.form.permissions],
          userCount: 0
        };
        
        this.roles.push(newRole);
        alert('角色创建成功');
      }
      
      this.closeModal();
    }
  }
}
</script> 